/* Services (CMS API calls) */
import { fetchPages } from 'services/page';
import { fetchSlug } from 'services/slug';
import { fetchNavigation } from '@services/navigation';
import { fetchFooter } from '@services/footer';
import { fetchLocations, fetchLocationsByCity } from '@services/location';
import { fetchCookie } from '@services/cookie';

/* Components */
import Head from 'next/head';
import { NextSeo } from 'next-seo';
import SectionBox from '@components/SectionBox';
import TabsSectionBox from '@components/TabsSectionBox';
import HeaderBox from '@components/HeaderBox';
import Footer from '@components/Footer';
import PreviewButton from '@components/PreviewButton';
import PageLayout from '@components/core/layout/PageLayout';
import Navigation from '@components/Navigation';
import Overview from '@components/templates/Overview';
import Breadcrumbs from '@components/Breadcrumbs';
import { useState, useEffect } from 'react';

/* Helpers */
import { filterPreview } from '@helpers/preview';
import { getCanonical, getOpenGraphProp, getStructuredData } from '@helpers/seo';

export async function getStaticPaths() {
  const pages = await fetchPages();

  const paths: {
    params: { slug: string[] },
    locale: string
  }[] = [];

  // Add pages paths
  pages.forEach((page) => {

    // Add nested paths
    const slug = [ `${ page.slug.current }` ];
    let currentElement = page;
    while (currentElement.parent && currentElement.parent.slug.current !== '/') {
      slug.unshift(currentElement.parent.slug.current);
      currentElement = currentElement.parent;
    }

    paths.push({
      params: { slug: slug.length == 1 && slug[0] === '/' ? [] : slug }, // Home '/' isn't a valid slug
      locale: 'nl', // Default to Dutch since we removed i18n
    });
  });

  return {
    paths,
    // 'blocking' so never-published pages (not in `paths`) can still be rendered
    // on-demand in Draft Mode for preview; public requests for unpublished/unknown
    // slugs fall through to `notFound` (404) in getStaticProps.
    fallback: 'blocking',
  };
}

interface IStaticProps {
  params: {
    slug: string[],
  },
  locale: string,
  draftMode?: boolean,
}

export async function getStaticProps({ params, locale, draftMode = false }: IStaticProps) {
  // Draft Mode (set via the preview API route) makes this run at request time and
  // fetch draft content; without it we statically generate the published page.
  const preview = draftMode;

  const slug = params.slug
    ? params.slug[params.slug.length - 1]
    : '/';

  // Given slugs are unique per level but not on domain, so we can fetch the slug with the parent slug to make sure we get the correct page
  let pageProps = params.slug === undefined
    ? await fetchSlug(slug, locale, preview)
    : params.slug?.length === 1 ? await fetchSlug(slug, locale, preview, '/') // Level 1
      : await fetchSlug(slug, locale, preview, params.slug[params.slug.length - 2]); // Level 2 and above

  pageProps = filterPreview(pageProps, params);

  if (!pageProps) {
    return { notFound: true };
  }

  if (pageProps.type === 'gemeente') {
    pageProps.locations = await fetchLocationsByCity(slug, preview);
  }

  if (slug === 'kinderopvang-locaties') {
    // Add locations data to page
    pageProps.locations = await fetchLocations(preview);
  }

  const navigationQueryResult = filterPreview(await fetchNavigation(locale, preview),params);
  const footerQueryResult = filterPreview(await fetchFooter(locale, preview),params);
  const cookie = await fetchCookie(locale, preview);

  const theme = 'kinderopvang';

  const notFound = Object.keys(pageProps).length === 0 || (Array.isArray(pageProps) && pageProps.length === 0);

  return {
    props: {
      // siteTitle,
      pageProps,
      locale,
      isInPreviewMode: preview,
      theme,
      navigationQueryResult,
      footerQueryResult,
      params,
      cookie,
    },
    notFound,
  };
}

export default function DynamicPathPage({ pageProps, locale, isInPreviewMode, theme, navigationQueryResult, footerQueryResult, params }) {
  const {
    seo,
    pageTitle,
    tabs,
    sectionBox,
    headerBox,
    locations,
  } = pageProps;

  const [ selectedTab, setSelectedTab ] = useState('');

  useEffect(() => {
    if (tabs?.length > 0 && !selectedTab) {
      setSelectedTab(tabs[0].label);
    } else if (tabs?.length > 0) {
      setSelectedTab(tabs.find(tab => tab.label === selectedTab)?.label || tabs[0].label);
    }
  }, [ tabs, selectedTab ]);

  // Find the currently selected tab data
  const selectedTabData = tabs?.find(tab => tab.label === selectedTab);

  // Last item has background?
  let backgroundColorFooter = 'bg-white';
  if (sectionBox && sectionBox[sectionBox.length -1]) {
    if (sectionBox[sectionBox.length -1]._type === 'component') {
      backgroundColorFooter = sectionBox[sectionBox.length -1].sections[0]?.sectionBackgroundColor ? 'bg-primary-50' : 'bg-white';
    } else {
      backgroundColorFooter = sectionBox[sectionBox.length -1].sectionBackgroundColor ? 'bg-primary-50' : 'bg-white';
    }
  }

  // First item has background?
  let backgroundColorHeader = 'bg-white';
  if (sectionBox && sectionBox[0]) {
    if (sectionBox[0]._type === 'component') {
      backgroundColorHeader = sectionBox[0].sections[0]?.sectionBackgroundColor ? 'bg-primary-50' : 'bg-white';
    } else {
      backgroundColorHeader = sectionBox[0].sectionBackgroundColor ? 'bg-primary-50' : 'bg-white';
    }
  } else if (tabs && selectedTabData) {
    // Use the selected tab's first section for background color
    backgroundColorHeader = selectedTabData.sectionBox[0]?.sectionBackgroundColor ? 'bg-primary-50' : 'bg-white';
  }

  const handleTabSelect = (tab: string) => {
    setSelectedTab(tab);
  };

  const isIndexable = !seo?.noindex;
  // Allow structured data in development even when noindex is set (for local testing)
  const includeStructuredData = isIndexable || process.env.NODE_ENV !== 'production';
  const structuredData = getStructuredData(pageProps.type, includeStructuredData, {
    parent: pageProps.parent,
    slug: pageProps.slug?.current,
    pageTitle: seo?.seoTitle ?? pageTitle,
    locations: pageProps.locations,
    contactDetails: pageProps.contactDetails,
    availableProducts: pageProps.availableProducts,
    sectionBox: pageProps.sectionBox,
  });

  return (
    <>
      <NextSeo
        title={ seo?.seoTitle ?? pageTitle }
        description={ `${ seo?.description ?? '' }` }
        canonical={ `${ getCanonical(seo?.canonical, params.slug ) }` }
        noindex={ seo?.noindex || false }
        nofollow={ seo?.nofollow || false }
        { ...getOpenGraphProp(seo) }
      />
      {
        structuredData.length > 0 && (
          <Head>
            { structuredData.map((data, index) => (
              <script
                key={ index }
                type='application/ld+json'
                dangerouslySetInnerHTML={ { __html: JSON.stringify(data) } }
              />
            )) }
          </Head>
        )
      }
      <PageLayout theme={ theme }>
        {
          navigationQueryResult &&
          <Navigation { ...navigationQueryResult } theme={ theme } />
        }
        <Breadcrumbs slugs={ params.slug? params.slug : [ ]  } />
        <HeaderBox
          headers={ headerBox }
          locale={ locale }
          theme={ theme }
          backgroundColorHeader={ locations && locations.length > 0 ? 'bg-white' : backgroundColorHeader }
        />
        {
          locations && locations.length > 0 && (pageProps.type === 'overzicht' || pageProps.type === 'gemeente') &&
          <Overview
            key={ theme }
            locations={ locations }
            domain={ theme }
            city={ pageProps.type === 'gemeente' ? pageProps.slug.current : '' }
          />
        }
        {
          sectionBox && (
            <SectionBox
              sections={ sectionBox }
              locale={ locale }
            />
          )
        }
        {
          tabs && (
            <TabsSectionBox
              labels={ tabs.map((tab) => tab.label) }
              sections={ tabs.map((tab) => tab.sectionBox) }
              locale={ locale }
              backgroundColorHeader={ backgroundColorHeader }
              onTabSelect={ handleTabSelect }
              activeTab={ selectedTab }
            />
          )
        }
        {
          footerQueryResult &&
          <Footer { ...footerQueryResult } theme={ theme } className={ backgroundColorFooter } />
        }
        {
          isInPreviewMode &&
          <PreviewButton slug={ params?.slug ? String(params.slug) : '/' } locale={ locale } />
        }
      </PageLayout>
    </>
  );
}
